/**
 * The Alpha warehouse: the three.js stage behind the Alpha DNA teaser.
 * The page plugs in a Hero (the card, cardHero.ts): what stands in the middle of
 * the shot, where the camera settles, and the close-up it glides into.
 * Everything else lives here.
 *
 * Asset contract (public/3d/alpha-dna/, produced by a Blender pipeline outside this repo):
 *   room.glb        geometry; TEXCOORD_0 = world-metre UVs for tiling detail
 *                   textures, TEXCOORD_1 = lightmap UVs. Materials are
 *                   placeholders matched by name below.
 *   lightmap.*      baked Cycles diffuse light from the key strip row (the
 *                   only lights that are ever on), sRGB-encoded and scaled
 *                   by manifest.lightmapScale
 *   puddles.*       floor wet mask on the lightmap UVs
 *   env.*           equirect panorama in the hall, used for reflections
 *   tex/*           tiling detail textures (colour, normal, ARM)
 *
 * The room is lit by its lightmap; the environment map only adds reflections
 * on it (its diffuse term is zeroed), so nothing is lit twice. Heroes bring
 * their own real-time lights. The floor mirrors the scene through a
 * reduced-resolution planar Reflector.
 *
 * Timeline after start(): the camera glides in from the entrance on a very
 * wide lens and settles on the hero's framing (long ease-out). As it settles,
 * the key row strikes up with a flicker, bloom and light curtains come on and
 * the dust catches the light.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import { createCurtain, createDust, createPost } from './effects';

// not public/alpha-dna/: a folder there would shadow the /alpha-dna route on the dev server
const BASE = '/3d/alpha-dna/';

/** Blender (Z-up, metres) -> three.js (Y-up). */
export const b2t = (x, y, z) => new THREE.Vector3(x, z, -y);

const MOVE_MS = 6200; // camera glide
const POWER_AT = 0.72; // fraction of the glide when the lights kick in
const UI_AT = 0.2; // fraction of the glide when the page's copy and sign-up come in (onIntroDone)
const POWER_MS = 1300; // flicker-to-full duration
const IDLE_POWER = 0.3; // key row level while the camera is still moving
const LIGHT_GAIN = 2.2; // lifts the baked key light so the hall reads, without relighting
const FLOOR_LAYER = 1; // the floor samples the mirror, so the mirror pass must not draw it
/** objects on this layer are seen by the camera but not mirrored in the wet floor */
export const NO_MIRROR_LAYER = 2;

const MATERIALS = {
  Floor_WetConcrete: { tex: 'floor', size: 2.0, color: [0.55, 0.56, 0.58], roughness: 0.5, envIntensity: 1.0 },
  Wall_CorrugatedGraphite: { tex: 'wall', size: 3.0, color: [0.45, 0.48, 0.52], roughness: 0.9, envIntensity: 0.35 },
  Roof_CorrugatedDark: { tex: 'wall', size: 3.0, color: [0.26, 0.27, 0.3], roughness: 1.0, envIntensity: 0.3 },
  Steel_NavyPainted: { tex: 'steel', size: 2.5, color: [0.55, 0.55, 0.55], roughness: 0.85, envIntensity: 0.45 },
};

const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
export const smooth = (a, b, t) => {
  const x = THREE.MathUtils.clamp((t - a) / (b - a), 0, 1);
  return x * x * (3 - 2 * x);
};

/** Fluorescent tubes striking: a few stutters, then full. u = 0..1 over POWER_MS. */
function flicker(u) {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  const keys = [
    [0, 0],
    [0.05, 1],
    [0.1, 0.15],
    [0.17, 0.9],
    [0.22, 0.05],
    [0.3, 0.05],
    [0.36, 1],
    [0.42, 0.55],
    [0.5, 1],
    [1, 1],
  ];
  for (let i = 1; i < keys.length; i++) {
    if (u <= keys[i][0]) {
      const [t0, v0] = keys[i - 1];
      const [t1, v1] = keys[i];
      return v0 + ((u - t0) / (t1 - t0)) * (v1 - v0);
    }
  }
  return 1;
}

/** Drop the image-based diffuse term: the lightmap already carries all diffuse light. */
function reflectionsOnly(shader) {
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <lights_fragment_maps>', '#include <lights_fragment_maps>\n\tiblIrradiance = vec3( 0.0 );')
    // A hero's spotlight mirrored in a puddle (roughness 0.03) is a pin-point hundreds of times over
    // the bloom threshold, and bloom smeared it into a big white cloud over the floor: cap the glint.
    .replace(
      '#include <lights_fragment_end>',
      '#include <lights_fragment_end>\n\treflectedLight.directSpecular = min( reflectedLight.directSpecular, vec3( 0.9 ) );',
    );
}

function streakTexture() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 32;
  const ctx = c.getContext('2d');
  // anamorphic streak: long, razor thin, blue-tinged
  const gx = ctx.createLinearGradient(0, 0, 512, 0);
  gx.addColorStop(0, 'rgba(60,100,255,0)');
  gx.addColorStop(0.3, 'rgba(100,145,255,0.25)');
  gx.addColorStop(0.5, 'rgba(235,242,255,1)');
  gx.addColorStop(0.7, 'rgba(100,145,255,0.25)');
  gx.addColorStop(1, 'rgba(60,100,255,0)');
  ctx.fillStyle = gx;
  ctx.fillRect(0, 0, 512, 32);
  ctx.globalCompositeOperation = 'destination-in';
  const gy = ctx.createLinearGradient(0, 0, 0, 32);
  gy.addColorStop(0, 'rgba(0,0,0,0)');
  gy.addColorStop(0.5, 'rgba(0,0,0,1)');
  gy.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gy;
  ctx.fillRect(0, 0, 512, 32);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A soft dusty shaft of light hanging from `top` to the floor (heroes stand in front of one). */
export function createBeam(shared, x, z, height) {
  const geo = new THREE.CylinderGeometry(0.05, 0.75, height, 48, 1, true);
  const mat = new THREE.ShaderMaterial({
    uniforms: { ...shared },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vNormalV;
      varying vec3 vPosV;
      void main() {
        vUv = uv;
        vec4 mv = modelViewMatrix * vec4( position, 1.0 );
        vPosV = mv.xyz;
        vNormalV = normalize( normalMatrix * normal );
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float level;
      uniform float time;
      varying vec2 vUv;
      varying vec3 vNormalV;
      varying vec3 vPosV;
      void main() {
        float facing = abs( dot( normalize( vNormalV ), normalize( -vPosV ) ) );
        float body = pow( facing, 3.0 );
        float along = smoothstep( 0.0, 0.25, vUv.y ) * ( 0.25 + 0.75 * vUv.y );
        float dust = 0.8 + 0.2 * sin( vUv.x * 60.0 + vUv.y * 9.0 + time * 0.6 ) * sin( vUv.y * 23.0 - time * 0.4 );
        float on = smoothstep( 0.5, 1.0, level );
        gl_FragColor = vec4( vec3( 0.72, 0.82, 1.0 ) * body * along * dust * on * 0.22, 1.0 );
      }`,
  });
  const beam = new THREE.Mesh(geo, mat);
  beam.position.set(x, height / 2, z);
  beam.renderOrder = 5;
  return beam;
}

export function createWarehouse(container, cb, opts) {
  const mobile = window.matchMedia('(max-width: 47.9375rem), (pointer: coarse)').matches;
  // dev aid: ?off=bloom,grade,curtains,beam,dust,mirror switches pieces off for tuning
  const off = new Set(new URLSearchParams(window.location.search).get('off')?.split(',') ?? []);
  const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
  // Resolution budget: big high-DPI monitors would otherwise render 5K+ frames through
  // bloom and the mirror pass. `quality` drops automatically if frames run slow.
  // Desktop allows most Retina laptop windows near full sharpness (2.6M rendered a MacBook-sized
  // window at ~70% and looked soft next to phones; 6M made frames heavy enough that `adapt`
  // then cut the resolution much harder).
  const MAX_PIXELS = mobile ? 1.4e6 : 4.2e6;
  let quality = 1;
  let pixelRatio = 1;
  const pickPixelRatio = (w, h) =>
    Math.max(
      0.5,
      Math.min(window.devicePixelRatio, mobile ? 1.5 : 2, Math.sqrt(MAX_PIXELS / Math.max(1, w * h))) * quality,
    );
  pixelRatio = pickPixelRatio(container.clientWidth, container.clientHeight);
  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // Khronos PBR Neutral keeps print and paint colours true (AgX drained them to grey)
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = opts.exposure ?? 1.15;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);
  const camera = new THREE.PerspectiveCamera(50, 1, 0.05, 120);
  camera.layers.enable(FLOOR_LAYER);
  camera.layers.enable(NO_MIRROR_LAYER);
  const post = createPost(renderer, scene, camera, {
    mobile,
    bloom: !off.has('bloom'),
    grade: !off.has('grade'),
    grain: opts.grain,
    lens: opts.lens,
  });

  const manager = new THREE.LoadingManager();
  manager.onProgress = (_url, loaded, total) => cb.onProgress(total ? loaded / total : 0);

  // WebP textures + meshopt geometry: no WASM transcoder to download, the
  // browser decodes images natively.
  const gltf = new GLTFLoader(manager).setMeshoptDecoder(MeshoptDecoder);
  const texLoader = new THREE.TextureLoader(manager);

  const disposables = [post];
  const reflectScale = mobile ? 0.4 : 0.5;
  let dustRatio = null;
  let reflector = null;
  let hero = null;
  let raf = 0;
  let disposed = false;
  let startAt = -1;
  let introDone = false;
  const pointer = new THREE.Vector2();
  const pointerSmooth = new THREE.Vector2();

  // live-tuned bits, filled in by load()
  const lit = [];
  let keyMat = null;
  const keyOn = new THREE.Color();
  const keyOff = new THREE.Color(0.03, 0.033, 0.04);
  const streaks = [];
  const shared = { time: { value: 0 }, level: { value: 0 } };

  // camera path: the hero fills in where it settles and its close-up
  const ENTRANCE = b2t(8, 1.2, 1.9);
  const camFrom = ENTRANCE.clone();
  let fovFrom = 90;
  const fr = {
    camTo: new THREE.Vector3(),
    lookTo: new THREE.Vector3(),
    fovTo: 60,
    lookFrom: new THREE.Vector3(),
    camFrom: null,
    camVia: null,
    fovFrom: null,
    focusPos: new THREE.Vector3(),
    focusLook: new THREE.Vector3(),
    fovFocus: 36,
    shiftTo: 0,
  };
  let focusTarget = 0;
  let focus = 0;
  let lastTick = 0;

  const loadTex = (url, srgb) =>
    new Promise((resolve, reject) => {
      texLoader.load(
        url,
        (t) => {
          t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
          t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
          t.flipY = false; // glTF UV convention
          disposables.push(t);
          resolve(t);
        },
        undefined,
        reject,
      );
    });

  const frame = () => {
    if (!hero) return;
    hero.frame(camera.aspect, fr);
    camFrom.copy(fr.camFrom ?? ENTRANCE);
    fovFrom = fr.fovFrom ?? Math.min(fr.fovTo + 30, 110);
  };

  const resize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    pixelRatio = pickPixelRatio(w, h);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(w, h);
    if (dustRatio) dustRatio.value = pixelRatio;
    post.setSize(w, h, pixelRatio);
    reflector
      ?.getRenderTarget()
      .setSize(Math.round(w * pixelRatio * reflectScale), Math.round(h * pixelRatio * reflectScale));
    camera.aspect = w / h;
    frame();
    camera.updateProjectionMatrix();
  };

  const onPointer = (e) => {
    pointer.set((e.clientX / window.innerWidth) * 2 - 1, (e.clientY / window.innerHeight) * 2 - 1);
  };
  window.addEventListener('pointermove', onPointer);

  async function load() {
    const manifest = await (await fetch(BASE + 'manifest.json')).json();
    const T = manifest.textures;
    camera.aspect = container.clientWidth / Math.max(1, container.clientHeight);
    camera.position.copy(camFrom);

    const ctx = { scene, renderer, camera, gltf, loadTex, shared, manifest, mobile, off, disposables };
    const [room, lightmap, puddles, env, heroLoaded, ...detail] = await Promise.all([
      gltf.loadAsync(BASE + 'room.glb'),
      loadTex(BASE + T.lightmap, true),
      loadTex(BASE + T.puddles, false),
      loadTex(BASE + T.env, true),
      opts.hero(ctx),
      ...['floor', 'wall', 'steel'].flatMap((k) => [
        loadTex(BASE + T[`${k}_color`], true),
        loadTex(BASE + T[`${k}_normal`], false),
        loadTex(BASE + T[`${k}_arm`], false),
      ]),
    ]);
    if (disposed) {
      heroLoaded.dispose();
      return;
    }
    hero = heroLoaded;
    disposables.push(hero);
    window.addEventListener('resize', resize);
    resize();
    camera.lookAt(fr.lookFrom);

    lightmap.channel = 1;
    const sets = {
      floor: detail.slice(0, 3),
      wall: detail.slice(3, 6),
      steel: detail.slice(6, 9),
    };
    for (const [name, [col, nor, arm]] of Object.entries(sets)) {
      const size = Object.values(MATERIALS).find((m) => m.tex === name).size;
      for (const t of [col, nor, arm]) {
        t.wrapS = t.wrapT = THREE.RepeatWrapping;
        t.repeat.set(1 / size, 1 / size);
      }
    }

    env.mapping = THREE.EquirectangularReflectionMapping;
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envRT = pmrem.fromEquirectangular(env);
    pmrem.dispose();
    disposables.push(envRT);
    scene.environment = envRT.texture;

    // Planar mirror over the floor. Its own draw is masked out (no colour/depth);
    // it only exists to render the reflection target that the floor shader samples.
    room.scene.updateMatrixWorld(true);
    let floorBox = null;
    room.scene.traverse((obj) => {
      const mesh = obj;
      if (mesh.isMesh && mesh.material.name === 'Floor_WetConcrete') {
        floorBox = new THREE.Box3().setFromObject(mesh);
      }
    });
    if (floorBox) {
      const box = floorBox;
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      reflector = new Reflector(new THREE.PlaneGeometry(size.x, size.z), {
        textureWidth: Math.round(container.clientWidth * pixelRatio * reflectScale),
        textureHeight: Math.round(container.clientHeight * pixelRatio * reflectScale),
        clipBias: 0.003,
        multisample: 0,
      });
      reflector.rotation.x = -Math.PI / 2;
      reflector.position.set(center.x, box.max.y + 0.0005, center.z);
      const rm = reflector.material;
      rm.colorWrite = false;
      rm.depthWrite = false;
      reflector.renderOrder = -1; // update the reflection before the floor draws
      scene.add(reflector);
      reflector.updateMatrixWorld(true);
      // the mirror camera is a clone of ours: drop the floor layer from it, otherwise the
      // floor would sample the very target being drawn (WebGL feedback loop)
      reflector.getReflectionCamera(camera).layers.disable(FLOOR_LAYER);
      reflector.getReflectionCamera(camera).layers.disable(NO_MIRROR_LAYER);
      disposables.push(reflector);
    }
    const reflUniforms = reflector
      ? {
          reflMap: { value: reflector.getRenderTarget().texture },
          reflTexMatrix: reflector.material.uniforms.textureMatrix,
          reflInvWorld: { value: reflector.matrixWorld.clone().invert() },
        }
      : null;

    const [sr, sg, sb] = manifest.stripColor;
    keyOn.setRGB(sr * 7, sg * 7, sb * 7); // HDR well above the bloom threshold: only the tubes glow
    room.scene.traverse((obj) => {
      const mesh = obj;
      if (!mesh.isMesh) return;
      const name = mesh.material.name;
      if (name === 'Light_Key') {
        keyMat = new THREE.MeshBasicMaterial({ color: keyOff.clone() });
        mesh.material = keyMat;
        disposables.push(keyMat);
        return;
      }
      if (name === 'Light_Off' || name === 'Window') {
        // dead tubes / night-dark glass
        const m = new THREE.MeshStandardMaterial({
          color: name === 'Window' ? 0x05070a : 0x15171b,
          roughness: name === 'Window' ? 0.08 : 0.4,
          metalness: 0,
          envMapIntensity: 0.6,
        });
        mesh.material = m;
        disposables.push(m);
        return;
      }
      const spec = MATERIALS[name];
      if (!spec) return;
      const [col, nor, arm] = sets[spec.tex];
      const lmIntensity = manifest.lightmapScale * Math.PI * LIGHT_GAIN;
      const mat = new THREE.MeshStandardMaterial({
        name,
        map: col,
        normalMap: nor,
        normalScale: new THREE.Vector2(spec.normal ?? 1, spec.normal ?? 1),
        roughnessMap: arm,
        metalnessMap: arm,
        roughness: spec.roughness,
        metalness: 1,
        color: new THREE.Color(...spec.color),
        lightMap: lightmap,
        lightMapIntensity: lmIntensity,
        envMapIntensity: spec.envIntensity,
      });
      lit.push({ mat, lm: lmIntensity, env: spec.envIntensity });
      if (name === 'Floor_WetConcrete') {
        mesh.layers.set(FLOOR_LAYER);
        mat.onBeforeCompile = (shader) => {
          reflectionsOnly(shader);
          shader.uniforms.puddleMap = { value: puddles };
          // freshly rained-on concrete: wide standing puddles, and the rest damp and glossy
          shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', '#include <common>\nuniform sampler2D puddleMap;')
            .replace(
              '#include <map_fragment>',
              `#include <map_fragment>
	float wet = smoothstep( 0.05, 0.5, texture2D( puddleMap, vLightMapUv ).r );
	float mirrorAmt = max( wet, 0.5 );
	diffuseColor.rgb *= mix( 0.75, 0.5, wet );`,
            )
            .replace(
              '#include <roughnessmap_fragment>',
              '#include <roughnessmap_fragment>\n\troughnessFactor = mix( roughnessFactor * 0.55, 0.03, wet );',
            )
            .replace(
              '#include <normal_fragment_maps>',
              `#include <normal_fragment_maps>
	vec3 bumpN = normal;
	normal = normalize( mix( normal, nonPerturbedNormal, wet * 0.85 ) );`,
            );
          if (off.has('mirror') || !reflUniforms) return;
          // swap the blurry environment reflection for the live planar mirror; on the damp
          // concrete the surface texture breaks it up a little (static, so it never shimmers)
          Object.assign(shader.uniforms, reflUniforms);
          shader.vertexShader = shader.vertexShader
            .replace(
              '#include <common>',
              '#include <common>\nuniform mat4 reflTexMatrix;\nuniform mat4 reflInvWorld;\nvarying vec4 vReflCoord;',
            )
            .replace(
              '#include <project_vertex>',
              '#include <project_vertex>\n\tvReflCoord = reflTexMatrix * reflInvWorld * modelMatrix * vec4( transformed, 1.0 );',
            );
          shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', '#include <common>\nuniform sampler2D reflMap;\nvarying vec4 vReflCoord;')
            .replace(
              '#include <lights_fragment_maps>',
              `#include <lights_fragment_maps>
	vec4 rc = vReflCoord;
	rc.xy += ( bumpN.xy - nonPerturbedNormal.xy ) * ( 1.0 - wet ) * 0.06 * rc.w;
	vec3 mirrorCol = texture2DProj( reflMap, rc ).rgb;
	radiance = mix( radiance, mirrorCol, mirrorAmt );
	// art-directed: a clearer mirror than Fresnel alone gives, so the hero reads in the wet floor
	totalEmissiveRadiance += mirrorCol * mix( 0.18, 0.5, wet );`,
            );
        };
      } else {
        mat.onBeforeCompile = reflectionsOnly;
      }
      mesh.material = mat;
      disposables.push(mat);
    });
    scene.add(room.scene);
    disposables.push({
      dispose: () => room.scene.traverse((o) => o.geometry?.dispose()),
    });

    // the lit row: thin anamorphic streaks (bloom gives the glow), curtains of light under each tube
    const streak = streakTexture();
    disposables.push(streak);
    const shafts = [...(hero.shafts ?? [])];
    for (const [x, y, z] of manifest.keyStrips) {
      const centrality = 1 - Math.min(1, Math.abs(x - 8) / 7);
      const tube = b2t(x, y, z - 0.03);
      const m = new THREE.SpriteMaterial({
        map: streak,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
      });
      const s = new THREE.Sprite(m);
      s.position.copy(tube);
      s.scale.set(5.5, 0.07, 1);
      s.renderOrder = 10;
      scene.add(s);
      streaks.push({ sprite: s, base: 0.25 + 0.5 * centrality });
      disposables.push(m);

      if (opts.curtains !== false && !off.has('curtains')) {
        const curtain = createCurtain(tube, 3.1, shared, 0.6 + 0.8 * centrality);
        scene.add(curtain);
        disposables.push(curtain.geometry, curtain.material);
      }
      shafts.push({ x: tube.x, z: tube.z, r: 1.1 });
    }

    // dust motes around the hero, glinting where they cross a shaft (kept off the lens)
    if (!off.has('dust') && hero.dustBox) {
      const count = hero.dustCount ?? 420;
      const dust = createDust(Math.round(mobile ? count * 0.52 : count), hero.dustBox, shafts, shared, pixelRatio);
      dustRatio = dust.material.uniforms.pixelRatio;
      scene.add(dust);
      disposables.push(dust.geometry, dust.material);
    }

    applyPower(IDLE_POWER, 0);

    // compile shaders and upload textures before the curtain lifts, so the intro never stutters
    await renderer.compileAsync(scene, camera);
    if (disposed) return;
    cb.onReady();
  }

  /** key-row level drives the bake, the tubes, the env reflections, curtains, dust and the hero's lights */
  function applyPower(power, flare) {
    for (const l of lit) {
      l.mat.lightMapIntensity = l.lm * power;
      l.mat.envMapIntensity = l.env * (0.2 + 0.8 * power);
    }
    keyMat?.color.lerpColors(keyOff, keyOn, power);
    const strike = Math.pow(Math.max(0, power - IDLE_POWER) / (1 - IDLE_POWER), 1.4);
    shared.level.value = power;
    hero?.setPower(power, strike);
    for (const f of streaks) f.sprite.material.opacity = f.base * flare;
  }

  // Adaptive resolution: if the GPU can't hold ~50 fps, step the internal resolution down
  // (and back up when there's headroom), so slower PCs get smooth instead of juddery.
  let lastNow = 0;
  let frameAcc = 0;
  let frameCount = 0;
  // Only steady slowness counts: hitches (a tab switch, a GC pause, a throttled background
  // pane) used to read as 100 ms frames and drove the resolution down to 45%, which looked
  // badly blurred. The floor is 75%: past that the picture goes soft for little gain.
  const adapt = (now) => {
    const dt = lastNow ? now - lastNow : 0;
    lastNow = now;
    if (dt <= 0 || dt > 60) return;
    frameAcc += dt;
    frameCount++;
    if (frameCount < 90) return;
    const avg = frameAcc / frameCount;
    frameAcc = 0;
    frameCount = 0;
    const next = avg > 24 ? quality - 0.1 : avg < 15 ? quality + 0.1 : quality;
    const clamped = THREE.MathUtils.clamp(next, 0.75, 1);
    if (Math.abs(clamped - quality) > 0.01) {
      quality = clamped;
      resize();
    }
  };

  const look = new THREE.Vector3();
  const anchorV = new THREE.Vector3();
  const tick = (now) => {
    raf = requestAnimationFrame(tick);
    if (!hero) {
      post.render(now / 1000);
      return;
    }
    if (startAt >= 0) adapt(now);
    const s = now / 1000;
    const elapsed = startAt >= 0 ? now - startAt : -1;
    const moveMs = opts.reducedMotion ? 1 : MOVE_MS;
    const t = elapsed < 0 ? 0 : Math.min(1, elapsed / moveMs);
    const k = easeOutQuint(t);

    if (fr.camVia) {
      // quadratic Bezier: a crane-like arc instead of a straight dolly
      const a = (1 - k) * (1 - k);
      const b = 2 * (1 - k) * k;
      const c = k * k;
      camera.position.set(
        camFrom.x * a + fr.camVia.x * b + fr.camTo.x * c,
        camFrom.y * a + fr.camVia.y * b + fr.camTo.y * c,
        camFrom.z * a + fr.camVia.z * b + fr.camTo.z * c,
      );
    } else {
      camera.position.lerpVectors(camFrom, fr.camTo, k);
    }
    look.lerpVectors(fr.lookFrom, fr.lookTo, k);
    camera.fov = THREE.MathUtils.lerp(fovFrom, fr.fovTo, k);

    // power-up as the glide settles
    const powerStart = moveMs * POWER_AT;
    const u = elapsed < 0 ? 0 : opts.reducedMotion ? 1 : (elapsed - powerStart) / POWER_MS;
    const strike = flicker(u);
    const power = IDLE_POWER + (1 - IDLE_POWER) * strike;
    const flareLevel = strike * smooth(0.3, 1, u) * (0.92 + 0.08 * Math.sin(s * 9.0) * Math.sin(s * 5.3));
    applyPower(power, flareLevel);
    shared.time.value = s;

    // idle life once settled: the camera barely drifts with the pointer
    pointerSmooth.lerp(pointer, 0.05);
    const live = (opts.reducedMotion ? 0 : 1) * smooth(0.55, 1, t);

    // close-up blend
    const dt = lastTick ? Math.min(0.05, (now - lastTick) / 1000) : 0;
    lastTick = now;
    const step = opts.reducedMotion ? 1 : dt / 1.1;
    focus += Math.sign(focusTarget - focus) * Math.min(Math.abs(focusTarget - focus), step);
    const fe = focus < 0.5 ? 4 * focus ** 3 : 1 - (-2 * focus + 2) ** 3 / 2;
    const drift = live * (1 - fe * 0.8);
    camera.position.x += (Math.sin(s * 0.3) * 0.04 + pointerSmooth.x * 0.05) * drift;
    camera.position.y += (Math.sin(s * 0.45) * 0.015 - pointerSmooth.y * 0.025) * drift;
    look.x += pointerSmooth.x * 0.04 * drift;
    if (fe > 0) {
      camera.position.lerp(fr.focusPos, fe);
      look.lerp(fr.focusLook, fe);
      camera.fov = THREE.MathUtils.lerp(camera.fov, fr.fovFocus, fe);
    }
    camera.lookAt(look);
    const shift = fr.shiftTo * k * (1 - fe);
    // the off-centre frustum: a view offset over a full view of the same size, moved down by `shift`
    if (Math.abs(shift) > 1e-4) camera.setViewOffset(camera.aspect, 1, 0, shift, camera.aspect, 1);
    else camera.clearViewOffset();

    hero.update(s, live, fe, pointerSmooth);
    if (opts.anchorEl && hero.anchor) {
      const show = hero.anchor(anchorV);
      if (show) {
        anchorV.project(camera);
        const ax = (anchorV.x * 0.5 + 0.5) * container.clientWidth;
        const ay = (-anchorV.y * 0.5 + 0.5) * container.clientHeight;
        opts.anchorEl.style.transform = `translate(${ax.toFixed(1)}px, ${ay.toFixed(1)}px) translate(-50%, -50%)`;
      }
    }

    post.render(s);

    // the sign-up doesn't wait for the glide or the lights: it comes in early and the room finishes around it
    if (!introDone && elapsed >= 0 && t >= UI_AT) {
      introDone = true;
      cb.onIntroDone();
    }
  };
  raf = requestAnimationFrame(tick);

  const onVisibility = () => {
    cancelAnimationFrame(raf);
    lastNow = 0; // don't count the time spent hidden as a slow frame
    if (!document.hidden && !disposed) raf = requestAnimationFrame(tick);
  };
  document.addEventListener('visibilitychange', onVisibility);

  load().catch((err) => {
    if (!disposed) cb.onError(err);
  });

  return {
    start() {
      if (startAt < 0) startAt = performance.now();
    },
    setFocus(on) {
      focusTarget = on ? 1 : 0;
    },
    camera,
    canvas: renderer.domElement,
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointer);
      document.removeEventListener('visibilitychange', onVisibility);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
